import React from 'react';
import { ShoppingCart, Zap } from 'lucide-react';
import { Product } from '../types';

interface ProductCardProps {
  product: Product;
  onOrder: (e: React.MouseEvent) => void;
  compactButton?: boolean;
}

const ProductCard: React.FC<ProductCardProps> = ({ product, onOrder, compactButton }) => {
  const isSticker = product.type === 'Sticker';
  const showCompactButton = isSticker || compactButton;

  return (
    <div
      className={`group relative bg-neutral-800 rounded-2xl overflow-hidden shadow-xl transition-all duration-300 h-full border border-white/5 hover:border-white/20`}
    >
      {/* Image Section */}
      <div
        className={`relative ${isSticker ? 'h-[160px] md:h-[220px]' : 'h-[300px] md:h-[400px]'
          } overflow-hidden`}
      >
        <div className="absolute inset-0 bg-gradient-to-t from-neutral-900 via-transparent to-transparent opacity-60 z-10 pointer-events-none"></div>

        {/* Both Posters and Stickers now use simple, elegant static images (no confusing hover reveals required) */}
        <img
          src={product.imageAfter}
          alt={product.name}
          className={`absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105 ${isSticker ? 'grayscale-[20%] group-hover:grayscale-0' : ''
            }`}
        />

        {/* Vibe Score (Posters only) */}
        {!isSticker && (
          <div className="absolute top-3 right-3 z-20 bg-black/50 backdrop-blur-md px-3 py-1 rounded-full border border-white/10 flex items-center gap-1.5">
            <Zap className="w-3 h-3 text-yellow-400 fill-yellow-400" />
            <span className="text-[10px] font-bold tracking-wider text-white">
              VIBE: {product.vibeScore}
            </span>
          </div>
        )}
      </div>

      {/* Content */}
      <div className="p-3 md:p-4 md:px-5 flex flex-col justify-between" style={{ minHeight: isSticker ? 'auto' : '140px' }}>
        <div>
          <h3
            className={`font-bold mb-1 truncate ${isSticker ? 'text-xs md:text-sm' : 'text-lg md:text-xl'
              }`}
          >
            {product.name}
          </h3>

          {!isSticker && (
            <p className="text-xs md:text-sm text-neutral-400 mb-4 line-clamp-2 md:line-clamp-3">
              {product.description}
            </p>
          )}
        </div>

        {/* Price + Add to Bag */}
        <div className="flex items-center justify-between border-t border-white/10 pt-3 md:pt-4 mt-auto">
          <div>
            <span className="text-[9px] text-neutral-500 uppercase font-bold tracking-widest hidden md:block mb-0.5">
              Price
            </span>
            <div className="flex items-center gap-2">
              <p className={`font-black ${isSticker ? 'text-xs md:text-sm' : 'text-sm md:text-lg text-white'}`}>
                ₹{product.price}
              </p>
              {product.originalPrice && (
                <p className="text-neutral-500 line-through text-[10px] md:text-xs font-mono font-medium">
                  ₹{product.originalPrice}
                </p>
              )}
            </div>
          </div>

          <button
            onClick={onOrder}
            aria-label={`Add ${product.name} to Bag`}
            className={`flex items-center justify-center gap-1.5 md:gap-2 bg-amber-600 hover:bg-amber-500 text-white rounded-lg font-bold transition-colors whitespace-nowrap shadow-lg shadow-amber-900/20 active:scale-95 ${showCompactButton
                ? 'px-3 py-1.5 md:px-4 md:py-2 text-[10px] md:text-xs'
                : 'px-3 py-2 md:px-5 md:py-2.5 text-xs md:text-sm'
              }`}
          >
            <ShoppingCart className={showCompactButton ? 'w-3 h-3 md:w-4 md:h-4' : 'w-3.5 h-3.5 md:w-4 md:h-4'} />
            {!showCompactButton && 'Add to Bag'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ProductCard;
